import {
    ActionType,
    ActivationState,
    AddressIPType,
    AddressType,
    IPDirection,
    LevelName,
    PolicyType,
    VersionState,
    VersionStatus
} from 'src/utils/enums';
import {AppDto} from './app.dto';

export type ActionOption = 'allow' | 'drop';

export interface FwVersion {
    baseVersionExternalId: string | null,
    content: FwVersionContent,
    customerId: number;
    comments: string;
    externalId: string;
    metadata: {
        createTimestamp: string;
        createdBy: string;
        updatedBy: string;
        updatedTimestamp: string;
    }
    versionNumber: number;
    versionName?: string;
    versionStatus: VersionStatus;
    versionState: VersionState;
    sid: number;
    serviceLevel: LevelName;
    versionType?: PolicyType | null;
    batchId?: string;
}

export interface FwVersionContentAndMetadata {
    content: FwVersionContent,
    metadata: {
        updatedBy: string,
        updatedTimestamp: string
    },
}

export interface FwVersionContent {
    policy: Policy;
    managed_apps: AppDto[];
}

export interface Policy {
    addresses: Address[];
    address_groups: AddressGroup[];
    services: Service[];
    service_groups: ServiceGroup[];
    rules: Rule[];
    url_filtering: UrlFiltering[];
    url_categories: UrlCategory[];
    profiles?: ProfileAntispywareAndDns[];
}

export interface ProfileAntispywareAndDns {
    antispyware: string,
    description: string,
    dns: string,
    dns_domain_exceptions: any[]
    id: string
}

export interface CategoryUrl {
    name: string;
    action: string;
}
interface UrlFiltering {
    categories: CategoryUrl[];
    description: string;
    displayName: string;
    id: string;
}

export interface UrlCategory {
    id: string;
    domains: string[];
    domainsDetails: Domain[];
}

export interface Address {
    id: string;
    displayName: string;
    type: AddressType;
    value: string;
    ipType?: AddressIPType;
    ipVersion: number;
    description: string;
    associatedAddressGroupList?: string[];
}

export interface AddressGroup {
    id: string;
    isDefault: boolean;
    displayName: string;
    description: string;
    addresses: string[];
}

export interface Service {
    id: string;
    displayName: string;
    description: string;
    proto: string;
    sports: string | string[];
    dports: string | string[];
    serviceStateOutbound?: string;
    serviceStateInbound?: string;
    isDefault: boolean;
}

interface ServiceGroup {
    id: string;
    description: string;
    services: string[];
}

export enum SecurityType {
    AntiVirus = 'anti_virus',
    AntiSpyware = 'anti_spyware',
    DNS = 'dns',
    VulnerabilityProtection = 'vulnerability_protection',
}

export const securityTypeMapping: { [key in SecurityType]: string } = {
    [SecurityType.AntiSpyware]: 'Anti-Spyware',
    [SecurityType.AntiVirus]: 'Anti-Virus',
    [SecurityType.VulnerabilityProtection]: 'Vul. Protection',
    [SecurityType.DNS]: 'DNS Protection',
};


export interface ProfilesOfRule {
    url_filtering: string,
    [SecurityType.AntiSpyware]?: string;
    [SecurityType.AntiVirus]?: string;
    [SecurityType.VulnerabilityProtection]?: string;
    [SecurityType.DNS]?: string;
}

export interface Rule {
    id: string;
    displayName: string;
    action: ActionOption;
    description: string;
    isDefault: boolean;
    position?: number;
    src: RuleDataDao;
    dst: RuleDataDao;
    service: ServiceOfRule;
    application: ApplicationOfRule;
    profiles: ProfilesOfRule,
   
    lastUpdaterMetadata: {
        firstName?: string;
        lastName?: string | null;
        email?: string | null;
        userId?: string | null;
    },
    disabled?: boolean;
    modified?: boolean;
    updatedDate: string;
}

export interface UpdatedRule extends Rule {
    originalDisplayName: string;
    newPosition: number | undefined | null;
}

export interface RuleDataDao {
    zone: string[];
    address: AddressOfRule;
}

interface AddressOfRule {
    regions: string[];
    addresses: string[];
    address_groups: string[];
    edls: string[];
}

export interface ServiceOfRule {
    services: string[];
    service_groups: string[];
}

export interface ApplicationOfRule {
    applications: string[];
}

export interface IP extends Address {
    direction: IPDirection;
    actionType: ActionType;
}

export interface Domain {
    name: string;
    domainUrl: string;
    domainAction: string;
    description: string;
}

export interface FwVersionInfo {
    batchId?: any;
    baseVersionExternalId: string | null;
    comments: string;
    externalId: string;
    versionNumber: number;
    versionStatus: VersionStatus;
    versionState: VersionState;
    sid: number;
    updatedTimestamp: string;
    lastUpdatedBy: string;
    serviceLevel: LevelName;
    versionName: string;
    activationTime?: string;
    versionType?: PolicyType | null;
}

export interface ActivateDeactivatePageDto {
    pageType: string;
    activationState: ActivationState;
}

export type InactiveOptionPolicySummary = '' | 'INACTIVE' | 'PARTIALLY INACTIVE';
export interface PolicySummaryForDashboardDto {
    versionNumber: number;
    lastUpdateDate: string;
    policyTypes: PolicyItem[];
    advancedThreatFeatures?: AdvancedThreatFeaturesDto;
    ddShieldInactiveStatus: InactiveOptionPolicySummary;
}

export interface PolicyItem {
    policyType: string;
    blocked: number;
    allowed: number;
    inactiveStatus: InactiveOptionPolicySummary;
}

export type AdvancedThreatAction = 'alert' | 'block' | 'manual' | 'inactive';

export interface AdvancedThreatFeaturesDto {
    anti_spyware: AdvancedThreatAction;
    anti_virus: AdvancedThreatAction;
    dns: AdvancedThreatAction;
    vulnerability_protection: AdvancedThreatAction;
}